'use client';

import React, { useState } from 'react';
import NextImage from 'next/image';
import { useResolvedLocalizedObject } from 'helpers/hooks/useResolvedLocalizedObject';
import useDimensions from './hooks/useDimensions';
import { cloudinaryLoader } from './loaders/cloudinary';
import defaultLoader from './loaders/default';
import { ImageProps } from './types';
import { isCloudinaryUrl } from './utils';

const Image = ({
  media,
  ratio,
  gravity,
  suffix,
  src = '',
  width,
  height,
  alt = '',
  scaleBy = 'width',
  cloudinaryPreset = 'default',
  title,
  onErrorSrc = '/images/placeholder.png',
  onError: externalOnError,
  ...props
}: ImageProps) => {
  const [hasError, setHasError] = useState(false);
  const dimensions = useDimensions({ media, width, height, ...props });

  const resolvedTitle = useResolvedLocalizedObject(title ?? '');

  const resolvedAlt = useResolvedLocalizedObject(alt ?? '');

  const isStaticImage = src.startsWith('/');

  // Combined error handler: sets error state AND calls external handler if provided
  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (onErrorSrc && !hasError) {
      setHasError(true);
    }
    externalOnError?.(e);
  };

  // Use fallback src if error occurred and fallback is provided
  const effectiveSrc = hasError && onErrorSrc ? onErrorSrc : src;

  // Render fallback image directly without Cloudinary processing.
  // This ensures the fallback always works reliably - if we routed it through
  // Cloudinary and that also failed, we'd have no fallback for the fallback.
  if (hasError && onErrorSrc) {
    return (
      <NextImage
        unoptimized
        src={onErrorSrc}
        loader={({ src }) => src}
        alt={resolvedAlt}
        title={resolvedTitle}
        {...dimensions}
        {...props}
      />
    );
  }

  if (media?.mediaId || (src && isCloudinaryUrl(src))) {
    return (
      <NextImage
        src={media?.mediaId ?? src}
        loader={({ width }) =>
          cloudinaryLoader({
            mediaId: media?.mediaId,
            src,
            width,
            ratio,
            scaleBy,
            height,
            gravity: gravity?.mode,
            x: gravity?.coordinates?.x?.toString(),
            y: gravity?.coordinates?.y?.toString(),
            preset: cloudinaryPreset,
          })
        }
        onError={handleError}
        alt={resolvedAlt}
        title={resolvedTitle}
        {...dimensions}
        {...props}
      />
    );
  }

  return (
    <NextImage
      unoptimized={!isStaticImage}
      src={defaultLoader({ src: effectiveSrc || onErrorSrc, suffix })}
      loader={({ src }) => src}
      alt={resolvedAlt}
      title={resolvedTitle}
      {...dimensions}
      onError={handleError}
      {...props}
    />
  );
};
export default Image;

export * from './types';
